import { type ReactNode } from "react";
import { NavDropdown } from "react-bootstrap";
import { css, cx } from "../../../../styled-system/css";
import { ProgressBar } from "react-aria-components";
import { ImSpinner2 } from "react-icons/im";

interface Props {
  mobileSidebarItem?: boolean;
  selectedKey: string;
  onSelectionChange: (key: string) => void;
  items: (
    | { value: string; label: string; isDisabled?: boolean; icon?: ReactNode; header?: false }
    | { header: true; label: string; value?: never; icon?: never }
  )[];
  isLoading?: boolean;
}

function NavFilterTile({ selectedKey, onSelectionChange, items, mobileSidebarItem, isLoading }: Props) {
  const onNavClick = (key: string) => {
    onSelectionChange(key);
    // TODO: Below closes mobile menu on org, channel, or marketplace change. This might not be needed but seems more intuitive on change.
    // Out of scope for this MR, but maybe a context for controlling navs would be better than toggling the class below? It's hard to guess that's what
    // triggers the change unless you know where to look
    mobileSidebarItem && document.documentElement.classList.toggle("nav-open");
  };

  const selectedItem = items.find((item) => item.value === selectedKey);

  return mobileSidebarItem ?
      <div
        className={css({
          display: "contents",
          zIndex: 3,
          position: "relative",
          margin: "1rem 1rem",
          fontSize: "medium",
          boxShadow: "0 0 0 0 rgba(0,0,0,0.5)",
        })}
      >
        {/* In mobile sidebar, only show options that can be selected to avoid overflow */}
        {items
          .filter((item) => !item.header && !item.isDisabled && item.value !== selectedKey)
          .map((item) => {
            if (item.header) return null;
            return (
              <NavDropdown.Item
                key={item.value}
                disabled={item.isDisabled}
                onClick={() => onNavClick(item.value)}
                id={item.value}
                className={"text-dark-purple"}
              >
                {item.icon && item.icon}
                {item.label}
              </NavDropdown.Item>
            );
          })}
      </div>
    : <NavDropdown
        title={
          <>
            {selectedItem?.icon}
            {selectedItem?.label}
            {isLoading && (
              <ProgressBar aria-label="Saving..." isIndeterminate className={css({ display: "contents" })}>
                <ImSpinner2
                  className={css({
                    animation: "spin",
                  })}
                />
              </ProgressBar>
            )}
          </>
        }
        disabled={isLoading}
        className={cx(
          `outline`,
          css({
            zIndex: 3,
            position: "relative",
            margin: "0",
            fontSize: "medium",
            boxShadow: "0 0 0 0 rgba(0,0,0,0.5)",
          }),
        )}
      >
        {items.map((item) => (
          <NavDropdown.Item
            key={item.value ?? `header-${item.label}`}
            disabled={item.header || item.isDisabled}
            onClick={() => {
              onNavClick(item.value!);
            }}
            id={item.value}
            style={item.header ? { marginTop: "1rem", fontSize: "20px", padding: ".5rem" } : { padding: ".5rem" }}
          >
            {item.icon && item.icon}
            {item.label}
          </NavDropdown.Item>
        ))}
      </NavDropdown>;
}

export default NavFilterTile;
